import React, { useState } from 'react'
import { useRouter } from '../hooks'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Query, Mutation } from '../api'
import { useTranslation } from 'react-i18next'
import { ChevronsUpDown, Check, Search, FolderKanban } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

const ProjectSelector = () => {
  const { t } = useTranslation()
  const { history } = useRouter()

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { data, loading } = useQuery(Query.ME)
  const [updateUser] = useMutation(Mutation.UPDATE_USER, {
    refetchQueries: [{ query: Query.DASHBOARD }, { query: Query.ME }],
  })

  const handleClick = async (id) => {
    setOpen(false)
    setSearch('')
    await updateUser({ variables: { projectId: id } })
    history.replace('/dashboard')
  }

  if (loading) return null

  const currentProject = data.me.currentProject
  const projects = data.projects || []
  const filtered = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch('') }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-9 bg-sidebar-accent/50 border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <span className="flex items-center gap-2 truncate">
            <FolderKanban className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate text-sm">
              {currentProject ? currentProject.name : t('Header_Project_Name_label')}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        {projects.length > 5 && (
          <div className="flex items-center border-b px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground mr-2" />
            <input
              type="text"
              placeholder={t('Header_Project_Name_placeholder', 'Search...')}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        <div className="max-h-[300px] overflow-y-auto p-1">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => handleClick(p.id)}
              className={cn(
                'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                currentProject && currentProject.id === p.id && 'font-medium',
              )}
            >
              {currentProject && currentProject.id === p.id && (
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  <Check className="h-4 w-4" />
                </span>
              )}
              {p.name}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="py-4 text-center text-sm text-muted-foreground">
              {t('Common_no_results', 'No results')}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default ProjectSelector
